import { environment } from '../../../environments/environment';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, OnInit, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { MessageService } from 'primeng/api';
import { InputText } from 'primeng/inputtext';
import { PasswordModule } from 'primeng/password';
import { Button } from 'primeng/button';
import { AuthService } from '../../core/services/auth';
import { BrandingService } from '../../core/services/branding';
import { TranslateModule } from '@ngx-translate/core';

/** Mots de passe du jeu de démonstration (voir backend/.env: DEMO_PASSWORD, DEMO_ADMIN_PASSWORD). */
const DEMO_PASSWORD = 'Password123!';
const DEMO_ADMIN_PASSWORD = 'Password123!';

@Component({
  selector: 'app-login',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    InputText,
    PasswordModule,
    Button, TranslateModule],
  template: `
    <div class="min-h-screen grid grid-cols-1 lg:grid-cols-[minmax(0,5fr)_minmax(0,6fr)] bg-canvas">

      <!-- Panneau de marque : visible dès lg, il porte l'identité et rassure. -->
      <aside class="hidden lg:flex relative flex-col justify-between overflow-hidden p-12 text-on-primary bg-gradient-to-br from-primary-base via-primary-hover to-primary-text" aria-hidden="true">
        <div class="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-white/10 blur-2xl"></div>
        <div class="absolute -bottom-32 -left-20 w-[28rem] h-[28rem] rounded-full bg-white/5 blur-3xl"></div>

        <div class="relative flex items-center gap-3">
          <div class="w-11 h-11 rounded-panel bg-white/15 backdrop-blur flex items-center justify-center">
            <i class="pi pi-heart-fill text-xl"></i>
          </div>
          <span class="text-xl font-bold tracking-tight">{{ brandName() }}</span>
        </div>

        <div class="relative max-w-md">
          <h2 class="text-4xl font-bold leading-tight tracking-tight">{{ 'AUTH.BRAND_TITLE' | translate }}</h2>
          <p class="mt-4 text-base text-white/80 leading-relaxed">
            {{ 'AUTH.BRAND_LEAD' | translate }}
          </p>
          <ul class="mt-8 flex flex-col gap-3 text-sm text-white/90">
            <li class="flex items-center gap-3"><i class="pi pi-check-circle"></i> {{ 'AUTH.BRAND_POINT_ISOLATED' | translate }}</li>
            <li class="flex items-center gap-3"><i class="pi pi-check-circle"></i> {{ 'AUTH.BRAND_POINT_OFFLINE' | translate }}</li>
            <li class="flex items-center gap-3"><i class="pi pi-check-circle"></i> {{ 'AUTH.BRAND_POINT_2FA' | translate }}</li>
          </ul>
        </div>

        <div class="relative text-xs uppercase tracking-widest text-white/60">2026 {{ brandName() }} Healthcare System</div>
      </aside>

      <!-- Colonne formulaire -->
      <main class="flex items-center justify-center p-5 sm:p-8">
        <div class="w-full max-w-md flex flex-col gap-6">

          <div class="lg:hidden flex items-center gap-3">
            <div class="w-10 h-10 rounded-panel bg-primary-base text-on-primary flex items-center justify-center">
              <i class="pi pi-heart-fill"></i>
            </div>
            <span class="text-lg font-bold text-ink">{{ brandName() }}</span>
          </div>

          <div class="bg-panel border border-hairline rounded-panel p-7 sm:p-8 shadow-sm">
            <h1 class="text-2xl font-bold text-ink tracking-tight">Bienvenue sur {{ brandName() }}</h1>
            <p class="mt-1.5 text-sm text-ink-muted">{{ 'AUTH.SUBTITLE' | translate }}</p>

            <form [formGroup]="loginForm" (ngSubmit)="onSubmit()" class="mt-7 flex flex-col gap-5">
              <div class="flex flex-col gap-1.5">
                <label for="email" class="text-xs font-semibold text-ink-muted">Adresse Email</label>
                <span class="relative">
                  <i class="pi pi-envelope absolute left-3 top-1/2 -translate-y-1/2 text-ink-subtle text-sm"></i>
                  <input pInputText id="email" formControlName="email" type="email" autocomplete="username" class="w-full !pl-9 !h-11" [placeholder]="'AUTH.EMAIL_PLACEHOLDER' | translate" />
                </span>
                @if (loginForm.get('email')?.hasError('required') && loginForm.get('email')?.touched) {
                  <small class="text-danger-text text-xs">{{ 'AUTH.ERR_EMAIL' | translate }}</small>
                }
                @if (loginForm.get('email')?.hasError('email') && loginForm.get('email')?.touched) {
                  <small class="text-danger-text text-xs">{{ 'VALIDATION.EMAIL' | translate }}</small>
                }
              </div>

              <div class="flex flex-col gap-1.5">
                <label for="password" class="text-xs font-semibold text-ink-muted">{{ 'AUTH.PASSWORD' | translate }}</label>
                <!-- inputId pose l'id sur l'input réel : le libellé devient cliquable et annonçable. -->
                <p-password inputId="password" formControlName="password" [toggleMask]="true" [feedback]="false" styleClass="w-full" inputStyleClass="w-full !h-11" [placeholder]="'AUTH.PASSWORD_PLACEHOLDER' | translate" />
                @if (loginForm.get('password')?.hasError('required') && loginForm.get('password')?.touched) {
                  <small class="text-danger-text text-xs">{{ 'AUTH.ERR_PASSWORD' | translate }}</small>
                }
              </div>

              <!-- Second facteur : n'apparaît qu'après que le serveur l'ait réclamé. -->
              @if (twoFactorRequired()) {
                <div class="flex flex-col gap-1.5 p-4 rounded-card bg-primary-surface border border-primary-border">
                  <label for="twoFactorCode" class="text-xs font-semibold text-primary-text">
                    {{ 'AUTH.TWO_FACTOR_TITLE' | translate }}
                  </label>
                  <p class="text-[11px] text-ink-muted leading-snug -mt-1 mb-1">
                    Saisissez le code à 6 chiffres de votre application d'authentification,
                    ou l'un de vos codes de secours.
                  </p>
                  <input pInputText id="twoFactorCode" formControlName="twoFactorCode"
                         inputmode="numeric" autocomplete="one-time-code"
                         class="w-full text-center tracking-[0.4em] font-bold"
                         placeholder="000000" />
                </div>
              }

              <p-button
                [label]="twoFactorRequired() ? 'Vérifier' : 'Se connecter'"
                type="submit"
                styleClass="w-full !h-12 !rounded-card !font-bold !text-base"
                [loading]="isLoading()"
                [disabled]="loginForm.invalid"
                [icon]="twoFactorRequired() ? 'pi pi-shield' : 'pi pi-sign-in'"
              />
            </form>
          </div>

          @if (demoMode) {
            <section class="bg-panel border border-hairline rounded-panel p-5" aria-labelledby="demo-title">
              <div class="flex items-center justify-between gap-3">
                <h2 id="demo-title" class="text-sm font-bold text-ink flex items-center gap-2">
                  <i class="pi pi-sitemap text-ink-subtle"></i> Comptes Demo
                </h2>
                <span class="text-[11px] text-ink-subtle">{{ 'AUTH.DEMO_HINT' | translate }}</span>
              </div>

              <div class="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
                @for (account of demoAccounts; track account.email) {
                  <button type="button" (click)="fillAccount(account)"
                       [attr.aria-label]="'Pré-remplir avec le compte ' + account.title"
                       class="group text-left p-2.5 bg-chrome border border-transparent rounded-card cursor-pointer hover:bg-panel hover:border-primary-border hover:shadow-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-base transition-all duration-200">
                    <div class="flex items-center gap-2.5">
                      <div class="w-8 h-8 rounded-control flex items-center justify-center text-sm shrink-0 transition-colors"
                        [class]="roleChipClass(account.role)">
                        <i [class]="'pi ' + account.icon"></i>
                      </div>
                      <div class="flex-1 min-w-0">
                        <div class="font-semibold text-ink text-xs leading-tight truncate">{{account.title}}</div>
                        <div class="text-[11px] text-ink-subtle truncate">{{account.email}}</div>
                      </div>
                    </div>
                  </button>
                }
              </div>

              <div class="mt-3 p-3 bg-warning-surface rounded-card border border-warning-border flex gap-2.5 items-start">
                <i class="pi pi-info-circle text-warning-text mt-0.5 text-sm"></i>
                <p class="text-xs text-warning-text leading-relaxed">{{ 'AUTH.DEMO_HINT_LONG' | translate }}</p>
              </div>
            </section>
          }

          <p class="lg:hidden text-center text-[10px] text-ink-subtle font-medium uppercase tracking-widest">
            2026 {{ brandName() }} Healthcare System
          </p>
        </div>
      </main>
    </div>
  `,
  styles: [`:host { display: block; }`]
})
export class LoginComponent implements OnInit {
  readonly demoMode = environment.demoMode;
  private fb = inject(FormBuilder);
  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);
  private authService = inject(AuthService);
  private router = inject(Router);
  private brandingService = inject(BrandingService);
  brandName = () => this.brandingService.branding().displayName;
  private messageService = inject(MessageService);

  loginForm: FormGroup = this.fb.group({
    email: ['', [Validators.required, Validators.email]],
    password: ['', [Validators.required]],
    twoFactorCode: ['']
  });

  readonly isLoading = signal(false);
  /** Set once the server answers that this account owes a second factor. */
  readonly twoFactorRequired = signal(false);
  // Default to 'simple' on first visit so new clinical/front-desk users
  // discover the kanban workflow by default. Their role gate still kicks
  // in at login: if they're not in simpleEligible, they land on /dashboard
  // anyway. Returning users keep whatever they picked last.
  readonly viewMode = signal<'normal' | 'simple'>(
    (localStorage.getItem('viewMode') as 'normal' | 'simple') || 'simple'
  );

  demoAccounts = [
    { title: 'Super Administrateur', role: 'super_admin', email: 'superadmin@climaxx.com', icon: 'pi-bolt' },
    { title: 'Administrateur Global', role: 'admin', email: 'admin@climaxx.com', icon: 'pi-shield' },
    { title: 'Médecin Praticien', role: 'doctor', email: 'doctor@climaxx.com', icon: 'pi-heart-fill' },
    { title: 'Personnel Infirmier', role: 'staff', email: 'aide@climaxx.com', icon: 'pi-users' },
    { title: 'Réceptionniste', role: 'receptionist', email: 'receptionist@climaxx.com', icon: 'pi-calendar' },
    { title: 'Technicien Labo', role: 'laborantin', email: 'lab@climaxx.com', icon: 'pi-search' },
    { title: 'Pharmacie', role: 'pharmacienne', email: 'pharmacy@climaxx.com', icon: 'pi-tablet' },
    { title: 'Espace Patient', role: 'patient', email: 'patient@climaxx.com', icon: 'pi-user' },
  ];

  /**
   * Pastille de rôle. Le gabarit liait `[class]` à un objet dont les clés
   * portaient plusieurs classes séparées par des espaces — Ivy ne découpe pas
   * ces clés, il cherche une classe nommée « bg-blue-100 text-blue-600 … » qui
   * n'existe pas : aucune pastille n'était peinte depuis le début. Une chaîne
   * calculée est à la fois correcte et lisible.
   *
   * Deux familles, pas sept : le vert soin pour qui touche au patient, le bleu
   * clinique pour qui fait tourner l'établissement.
   */
  private static readonly CARE_ROLES = ['doctor', 'staff', 'laborantin', 'pharmacienne'];

  roleChipClass(role: string): string {
    if (LoginComponent.CARE_ROLES.includes(role)) {
      return 'bg-success-surface text-success-text group-hover:bg-success-hover group-hover:text-white';
    }
    if (role === 'patient') {
      return 'bg-chrome text-ink-muted group-hover:bg-neutral-base group-hover:text-white';
    }
    return 'bg-primary-surface text-primary-text group-hover:bg-primary-base group-hover:text-on-primary';
  }

  ngOnInit(): void {
    this.loginForm.statusChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.cdr.markForCheck());
  }

  fillAccount(account: any) {
    // Mots de passe du jeu de démonstration (variables DEMO_PASSWORD / DEMO_ADMIN_PASSWORD
    // du seeder). Cet écran n'affiche les comptes qu'en mode démo (environnement local/test).
    const password = account.role === 'super_admin' ? DEMO_ADMIN_PASSWORD : DEMO_PASSWORD;
    this.loginForm.patchValue({ email: account.email, password });
    this.loginForm.get('password')?.markAsUntouched();
    this.cdr.markForCheck();
  }

  onSubmit() {
    if (this.loginForm.valid) {
      this.isLoading.set(true);
      this.authService.login(this.loginForm.value).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: (response) => {
          this.isLoading.set(false);

          // Credentials were right but the account has 2FA: reveal the code
          // field and stay on the form instead of navigating.
          if (response?.twoFactorRequired) {
            this.twoFactorRequired.set(true);
            this.loginForm.get('twoFactorCode')?.setValidators([Validators.required]);
            this.loginForm.get('twoFactorCode')?.updateValueAndValidity();
            this.messageService.add({
              severity: 'info',
              summary: 'Vérification en deux étapes',
              detail: 'Saisissez le code de votre application d\'authentification.',
              life: 6000,
            });
            this.cdr.markForCheck();
            return;
          }

          const role = response?.user?.role;
          const mode = this.viewMode();
          const simpleEligible = ['doctor', 'aide_soignante', 'receptionist', 'laborantin', 'pharmacienne'];
          let destination = '/dashboard';
          if (role === 'patient') {
            destination = '/patient-portal';
          } else if (mode === 'simple' && role && simpleEligible.includes(role)) {
            destination = '/simple';
          }
          this.router.navigate([destination]);
          this.messageService.add({ severity: 'success', summary: 'Connexion réussie', life: 3000 });
        },
        error: (err: HttpErrorResponse) => {
          this.isLoading.set(false);
          const rawMessage = err.error?.message;
          const detail = Array.isArray(rawMessage)
            ? rawMessage.join(', ')
            : rawMessage || 'Identifiants invalides';
          this.messageService.add({ severity: 'error', summary: 'Erreur de connexion', detail, life: 5000 });
        }
      });
    }
  }
}
